import ImageProvider from '@bluheadless/ui-logic/src/providers/image'
import { roundUp5 } from '@bluheadless/ui-logic/src/utils/math'
import { cx } from '@emotion/css'
import { elementType, object, oneOf, oneOfType, string } from 'prop-types'
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import {
	A11y,
	Autoplay,
	Controller,
	EffectCoverflow,
	EffectCube,
	EffectFade,
	EffectFlip,
	Navigation,
	Pagination,
	Scrollbar,
	Thumbs,
	Zoom,
} from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { ArrowheadDownIconSvg } from '../../atoms/icons/arrowhead-down-icon'
import { ArrowheadLeftIconSvg } from '../../atoms/icons/arrowhead-left-icon'
import { ArrowheadRightIconSvg } from '../../atoms/icons/arrowhead-right-icon'
import { ArrowheadUpIconSvg } from '../../atoms/icons/arrowhead-up-icon'
import { Container, NavButton, PaginationStyled, ScrollbarStyled, SwiperSlideStyled } from './slider.styled'

const Slide = SwiperSlideStyled

const getImageSizes = (slidesPerView = 1) => roundUp5(100 / slidesPerView)

export const DEFAULT_SCROLLBAR_BAR_PROPS = {
	draggable: true,
	dragSize: 'auto',
	el: '.swiper-scrollbar',
	hide: false,
}

const Slider = forwardRef(function Slider(
	{
		aboveTheFold,
		children,
		direction,
		iconNext,
		iconNextVertical,
		iconPrevious,
		iconPreviousVertical,
		navigation,
		pagination,
		containerProps,
		subContainerProps,
		controlsPosition,
		onSwiper,
		className,
		scrollbar,
		modules,
		...props
	},
	ref
) {
	const sliderRef = useRef()
	const prevRef = useRef()
	const nextRef = useRef()
	const paginationRef = useRef()
	const scrollbarRef = useRef()
	const { formatMessage } = useIntl()

	const setNavigation = () => {
		//to prevent two sliders from controlling each other
		if (sliderRef.current && prevRef.current && nextRef.current) {
			const slider = sliderRef.current
			const navigationParams = slider.params.navigation
			const sliderNavigation = slider.navigation

			navigationParams.prevEl = prevRef.current.firstChild //firstChild because current doesn't manage enabled/disabled status (known bug)
			navigationParams.nextEl = nextRef.current.firstChild
			sliderNavigation.destroy() //otherwise navigation is messed up
			sliderNavigation.init()
			sliderNavigation.update()
		}
	}

	const setPagination = () => {
		if (sliderRef.current && paginationRef.current) {
			const slider = sliderRef.current
			const sliderPagination = slider.pagination

			slider.params.pagination.el = paginationRef.current.firstChild
			sliderPagination.init()
			sliderPagination.render()
			sliderPagination.update()
		}
	}

	const setScrollbar = () => {
		if (sliderRef.current && scrollbarRef.current) {
			const slider = sliderRef.current
			const sliderScrollbar = slider.scrollbar

			slider.params.scrollbar.el = scrollbarRef.current.firstChild
			sliderScrollbar.destroy()
			sliderScrollbar.init()
			sliderScrollbar.updateSize()
		}
	}

	useEffect(() => {
		setNavigation()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [navigation, nextRef, prevRef, sliderRef])

	useEffect(() => {
		setPagination()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pagination, paginationRef, sliderRef])

	useEffect(() => {
		setScrollbar()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [scrollbar, scrollbarRef, sliderRef])

	const [imageSizes, setImageSizes] = useState(getImageSizes(direction === 'vertical' ? 1 : props.slidesPerView))

	const onKeyDown = useCallback((e, action, slide) => {
		if (e.key === 'Enter' || e.keyCode === '13') {
			if (action === 'prev') {
				sliderRef.current.slidePrev()
			} else if (action === 'next') {
				sliderRef.current.slideNext()
			} else if (action === 'slide') {
				sliderRef.current.slideTo(slide)
			}
		}
	}, [])

	const onPaginationRender = useCallback(
		(swiper, paginationEl) => {
			try {
				const bullets = paginationEl.querySelectorAll('.swiper-pagination-bullet')
				bullets.forEach((bullet, index) => {
					bullet.addEventListener('keydown', (e) => {
						onKeyDown(e, 'slide', index)
					})
				})
			} catch (error) {
				// do nothing
			}
		},
		[onKeyDown]
	)

	return (
		<Container aboveTheFold={aboveTheFold} {...containerProps}>
			<Container aboveTheFold={aboveTheFold} {...subContainerProps}>
				<ImageProvider sizes={imageSizes}>
					<Swiper
						className={cx(className, 'swiper-container')}
						modules={[
							A11y,
							Autoplay,
							Controller,
							EffectCoverflow,
							EffectCube,
							EffectFade,
							EffectFlip,
							Navigation,
							Pagination,
							Scrollbar,
							Thumbs,
							Zoom,
							...(modules || []),
						]}
						direction={direction}
						navigation={navigation !== true && navigation}
						pagination={pagination !== true && pagination}
						scrollbar={
							scrollbar === true
								? // default scrollbar settings
								  DEFAULT_SCROLLBAR_BAR_PROPS
								: scrollbar !== true && scrollbar
						}
						onBreakpoint={(swiper) => {
							setImageSizes(getImageSizes(direction === 'vertical' ? 1 : swiper.params.slidesPerView))
						}}
						{...props}
						a11y={{
							enabled: true,
							prevSlideMessage: formatMessage({ id: 'slider_prev_slide' }),
							nextSlideMessage: formatMessage({ id: 'slider_next_slide' }),
							firstSlideMessage: formatMessage({ id: 'slider_first_slide' }),
							lastSlideMessage: formatMessage({ id: 'slider_last_slide' }),
							paginationBulletMessage: formatMessage({ id: 'slider_go_to_slide' }, { slide: '{{index}}' }),
							slideRole: 'button',
							...(props?.a11y ?? {}),
						}}
						keyboard={{
							enabled: true,
							onlyInViewport: true,
							pageUpDown: props?.keyboard?.pageUpDown ?? props?.direction === 'vertical' ? true : false,
							...(props?.keyboard ?? {}),
						}}
						ref={ref}
						onPaginationRender={onPaginationRender}
						onSwiper={(...args) => {
							const swiper = args[0]
							sliderRef.current = swiper
							if (onSwiper) {
								onSwiper(...args)
							}
						}}
					>
						{children}
					</Swiper>
				</ImageProvider>

				{navigation === true && (
					<>
						<div ref={prevRef}>
							<NavButton
								role="button"
								tabIndex={0}
								action="prev"
								className="swiper-button-prev"
								component={direction === 'horizontal' ? iconPrevious : iconPreviousVertical}
								onKeyDown={(e) => onKeyDown(e, 'prev')}
							/>
						</div>
						<div ref={nextRef}>
							<NavButton
								role="button"
								tabIndex={0}
								action="next"
								className="swiper-button-next"
								component={direction === 'horizontal' ? iconNext : iconNextVertical}
								onKeyDown={(e) => onKeyDown(e, 'next')}
							/>
						</div>
					</>
				)}
			</Container>

			{pagination && controlsPosition === 'default' && (
				<div ref={paginationRef}>
					<PaginationStyled className="swiper-pagination" />
				</div>
			)}

			{scrollbar && controlsPosition === 'default' && (
				<div ref={scrollbarRef}>
					<ScrollbarStyled className="swiper-scrollbar" />
				</div>
			)}
		</Container>
	)
})

Slide.defaultProps = {
	...SwiperSlide.defaultProps,
}

Slide.prototypes = {
	...SwiperSlide.prototypes,
	containerProps: object,
	subContainerProps: object,
}

Slider.defaultProps = {
	...Swiper.defaultProps,
	direction: 'horizontal',
	iconNext: ArrowheadRightIconSvg,
	iconNextVertical: ArrowheadDownIconSvg,
	iconPrevious: ArrowheadLeftIconSvg,
	iconPreviousVertical: ArrowheadUpIconSvg,
	navigation: false,
	pagination: false,
	scrollbar: false,
	spaceBetween: 0,
	controlsPosition: 'default',
}

Slider.propTypes = {
	...Swiper.propTypes,
	/**
	 * Next icon
	 */
	iconNext: elementType,
	/**
	 * Next icon when slider is vertical
	 */
	iconNextVertical: elementType,
	/**
	 * Previous icon
	 */
	iconPrevious: elementType,
	/**
	 * Previous icon when slider is vertical
	 */
	iconPreviousVertical: elementType,
	/**
	 * Position of controls (arrows and pagination)
	 */
	controlsPosition: oneOfType([oneOf(['default', 'top-right']), string]),
}

export default Slider
export { Slide, Slider }
